import { useState, useEffect, useCallback } from "react";
import {
  RefreshCw, Play, Check, X, Loader2, Download, Link2, AlertTriangle,
  Clock, Globe, Search, Edit3, Shield, Archive, RotateCcw, ChevronDown,
  ChevronUp, ArrowUpCircle, Info, Mail, Tag, Filter, Upload,
  CheckCircle2, Circle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import { ensureFreshToken } from "@/lib/sessionKeepAlive";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuth() {
  return { authorization: `Bearer ${localStorage.getItem("admin_token") || localStorage.getItem("app_token")}` };
}

const CONNECTOR_LABELS = {
  levelup: "LevelUp Catalog API",
  skilling_hub: "Skilling Hub Events",
  mslearn: "Microsoft Learn",
  mslearn_certifications: "MS Learn Certifications",
  mslearn_applied_skills: "MS Learn Applied Skills",
};

// ── Change type badge colors ──
function ChangeTypeBadge({ type }) {
  const cfg = {
    new: { label: "New", color: "bg-[#D4EC8E] text-[#091F2C]" },
    updated: { label: "Updated", color: "bg-blue-100 text-blue-800" },
    missing: { label: "Missing", color: "bg-orange-100 text-orange-800" },
    manual_upload: { label: "Upload", color: "bg-purple-100 text-purple-800" },
    retired: { label: "Retired", color: "bg-red-100 text-red-700" },
  };
  const c = cfg[type] || { label: type || "—", color: "bg-[#F4F3F5] text-[#64748B]" };
  return <Badge className={`text-[10px] border-0 ${c.color}`}>{c.label}</Badge>;
}

function StatusBadge({ status }) {
  const cfg = {
    Staging: { label: "Staging", color: "bg-yellow-50 text-yellow-800 border border-yellow-200" },
    inactive_pending_review: { label: "Inactive — Review", color: "bg-orange-50 text-orange-800 border border-orange-200" },
    archive_candidate: { label: "Archive Candidate", color: "bg-red-50 text-red-700 border border-red-200" },
    Published: { label: "Published", color: "bg-[#D4EC8E] text-[#091F2C] border-0" },
    Active: { label: "Active", color: "bg-[#D4EC8E] text-[#091F2C] border-0" },
    Rejected: { label: "Rejected", color: "bg-red-100 text-red-700 border-0" },
    Archived: { label: "Archived", color: "bg-gray-100 text-gray-600 border-0" },
  };
  const c = cfg[status] || { label: status || "—", color: "bg-[#F4F3F5] text-[#64748B] border-0" };
  return <Badge className={`text-[10px] ${c.color}`}>{c.label}</Badge>;
}

function ConfidenceBadge({ score }) {
  const color = score >= 75 ? "bg-[#D4EC8E] text-[#091F2C]" : score >= 50 ? "bg-[#B9DCD2] text-[#091F2C]" : "bg-[#F4F3F5] text-[#64748B]";
  return <Badge className={`text-[10px] border-0 ${color}`}>{score}%</Badge>;
}

// ── Connector Card ──
function ConnectorCard({ connector, onSync }) {
  const [syncing, setSyncing] = useState(false);
  const [collectionUrl, setCollectionUrl] = useState("");
  const autoPublish = connector.auto_publish?.enabled || false;

  const handleSync = async () => {
    setSyncing(true);
    try {
      const payload = { connector: connector.connector, tag_with_ai: true };
      if (connector.connector === "mslearn") {
        if (!collectionUrl) {
          toast.error("Please enter a Learn Collection URL");
          setSyncing(false);
          return;
        }
        payload.collection_url = collectionUrl;
      }
      const res = await axios.post(`${API}/admin/connectors/sync`, payload, { headers: getAuth() });
      const ap = res.data.auto_publish ? " (auto-published)" : "";
      toast.success(`Sync complete: ${res.data.new_staged} new, ${res.data.updated_staged || 0} updated, ${res.data.unchanged || 0} unchanged${ap}`);
      onSync?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleAutoPublish = async () => {
    try {
      await axios.put(`${API}/admin/connectors/auto-publish`, {
        connector: connector.connector,
        enabled: !autoPublish,
      }, { headers: getAuth() });
      toast.success(`Auto-publish ${!autoPublish ? "enabled" : "disabled"} for ${CONNECTOR_LABELS[connector.connector] || connector.connector}`);
      onSync?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to toggle auto-publish");
    }
  };

  const lastSync = connector.last_sync;
  const lastRun = connector.last_run;
  const schedule = connector.schedule || {};

  return (
    <div className="bg-white rounded-xl border border-black/5 shadow-sm p-5" data-testid={`connector-card-${connector.connector}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-medium text-[#091F2C] text-sm">{CONNECTOR_LABELS[connector.connector] || connector.connector}</h4>
          <p className="text-xs text-[#64748B] mt-0.5">
            {connector.staging_count || 0} staging · {connector.published_count} published
            {connector.archive_candidate_count > 0 && (
              <span className="text-red-600 ml-1">· {connector.archive_candidate_count} archive</span>
            )}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge className={`text-xs ${schedule.enabled ? "bg-[#D4EC8E] text-[#091F2C] border-0" : "bg-[#F4F3F5] text-[#64748B] border-0"}`}>
            {schedule.enabled ? `Auto: ${schedule.frequency}` : "Manual"}
          </Badge>
          {autoPublish && (
            <Badge className="text-[10px] bg-[#07641D]/10 text-[#07641D] border-0" data-testid={`auto-publish-badge-${connector.connector}`}>
              Auto-Publish
            </Badge>
          )}
        </div>
      </div>

      {/* Last Run metrics */}
      {lastRun ? (
        <div className="grid grid-cols-3 gap-1.5 mb-3">
          {[
            { label: "Fetched", val: lastRun.records_fetched },
            { label: "New", val: lastRun.records_new, green: true },
            { label: "Updated", val: lastRun.records_updated, blue: true },
            { label: "Same", val: lastRun.records_unchanged },
            { label: "Missing", val: lastRun.records_missing, orange: lastRun.records_missing > 0 },
            { label: lastRun.status, val: null, status: true },
          ].map((m, i) => (
            <div key={i} className="bg-[#F4F3F5] rounded-md px-2 py-1.5 text-center">
              {m.val !== null ? (
                <>
                  <div className={`text-base font-semibold ${m.green ? "text-[#07641D]" : m.blue ? "text-blue-700" : m.orange ? "text-orange-600" : "text-[#091F2C]"}`}>{m.val}</div>
                  <div className="text-[10px] text-[#64748B]">{m.label}</div>
                </>
              ) : (
                <div className={`text-[10px] font-medium ${lastRun.status === "completed" ? "text-[#07641D]" : lastRun.status === "failed" ? "text-red-600" : "text-[#64748B]"} py-1.5`}>
                  {lastRun.status}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : lastSync ? (
        <div className="text-xs text-[#64748B] mb-3 flex items-center gap-1.5">
          <Clock className="w-3 h-3" />
          Last sync: {new Date(lastSync.timestamp).toLocaleString()} · {lastSync.new_count} new
        </div>
      ) : null}

      {connector.connector === "mslearn" && (
        <div className="mb-3">
          <Input
            value={collectionUrl}
            onChange={e => setCollectionUrl(e.target.value)}
            placeholder="https://learn.microsoft.com/en-us/collections/..."
            className="h-9 text-xs bg-[#F4F3F5] border-0"
            data-testid="mslearn-url-input"
          />
        </div>
      )}

      <div className="flex gap-2">
        <Button
          onClick={handleSync}
          disabled={syncing}
          size="sm"
          className="bg-[#194449] text-white hover:bg-[#07641D] rounded-full text-xs flex-1"
          data-testid={`sync-btn-${connector.connector}`}
        >
          {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
          Sync Now
        </Button>
        <Button
          onClick={handleToggleAutoPublish}
          variant="outline"
          size="sm"
          className={`rounded-full text-xs px-3 ${autoPublish ? "border-[#07641D] text-[#07641D] hover:bg-[#07641D]/5" : "border-[#64748B]/30 text-[#64748B] hover:bg-[#F4F3F5]"}`}
          data-testid={`auto-publish-toggle-${connector.connector}`}
          title={autoPublish ? "Auto-publish is ON — synced courses go directly to Active" : "Auto-publish is OFF — synced courses go to Staging for review"}
        >
          {autoPublish ? <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> : <Circle className="w-3.5 h-3.5 mr-1" />}
          AP
        </Button>
      </div>
    </div>
  );
}

// ── Field Diff View ──
function FieldDiffPopover({ fieldDiffs }) {
  const [open, setOpen] = useState(false);
  if (!fieldDiffs || Object.keys(fieldDiffs).length === 0) return null;
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="text-[10px] text-blue-600 underline flex items-center gap-0.5"
      >
        <Info className="w-3 h-3" /> {Object.keys(fieldDiffs).length} change{Object.keys(fieldDiffs).length > 1 ? "s" : ""}
      </button>
      {open && (
        <div className="absolute z-20 left-0 top-5 bg-white border border-black/10 rounded-lg shadow-lg p-3 min-w-[240px] text-xs">
          <div className="font-medium text-[#091F2C] mb-2">Field Changes</div>
          {Object.entries(fieldDiffs).map(([field, diff]) => (
            <div key={field} className="mb-1.5">
              <div className="text-[#64748B] font-medium">{field}</div>
              <div className="text-red-600 truncate max-w-[200px]">
                − {String(diff.old || "").slice(0, 60)}
              </div>
              <div className="text-[#07641D] truncate max-w-[200px]">
                + {String(diff.new || "").slice(0, 60)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminConnectors() {
  const [connectors, setConnectors] = useState([]);
  const [staging, setStaging] = useState([]);
  const [bulkHistory, setBulkHistory] = useState([]);
  const [confirmUndoId, setConfirmUndoId] = useState(null);
  const [archiveCandidates, setArchiveCandidates] = useState([]);
  const [changeLogs, setChangeLogs] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [syncRuns, setSyncRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [activeTab, setActiveTab] = useState("connectors");
  const [selectedResource, setSelectedResource] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editFields, setEditFields] = useState({});
  const [actionLoading, setActionLoading] = useState({});
  const [linkCheckResults, setLinkCheckResults] = useState(null);
  const [linkChecking, setLinkChecking] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [heroProductOptions, setHeroProductOptions] = useState([]);
  const [stagingFilter, setStagingFilter] = useState("all");

  const [testDigestLoading, setTestDigestLoading] = useState(false);

  // Taxonomy state
  const [taxonomy, setTaxonomy] = useState([]);
  const [taxonomyTotal, setTaxonomyTotal] = useState(0);
  const [taxonomyLoading, setTaxonomyLoading] = useState(false);
  const [taxonomyFilter, setTaxonomyFilter] = useState({ tech_name: "", product_family: "", role_name: "", is_active: "" });
  const [taxonomyFamilies, setTaxonomyFamilies] = useState([]);
  const [taxonomyRoles, setTaxonomyRoles] = useState([]);
  const [taxonomyImportFile, setTaxonomyImportFile] = useState(null);
  const [taxonomyImportMode, setTaxonomyImportMode] = useState("upsert");
  const [taxonomyImporting, setTaxonomyImporting] = useState(false);

  const handleTestDigest = async () => {
    setTestDigestLoading(true);
    try {
      const res = await axios.post(`${API}/admin/email/test-digest`, {}, { headers: getAuth() });
      toast.success(`Test digest sent to ${res.data.sent} admin${res.data.sent !== 1 ? "s" : ""}!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test digest");
    } finally {
      setTestDigestLoading(false);
    }
  };

  const loadTaxonomy = useCallback(async () => {
    setTaxonomyLoading(true);
    try {
      const h = getAuth();
      const params = new URLSearchParams({ limit: 500 });
      if (taxonomyFilter.tech_name) params.set("tech_name", taxonomyFilter.tech_name);
      if (taxonomyFilter.product_family) params.set("product_family", taxonomyFilter.product_family);
      if (taxonomyFilter.role_name) params.set("role_name", taxonomyFilter.role_name);
      if (taxonomyFilter.is_active !== "") params.set("is_active", taxonomyFilter.is_active);
      const [taxRes, famRes, roleRes] = await Promise.all([
        axios.get(`${API}/admin/taxonomy?${params}`, { headers: h }),
        axios.get(`${API}/admin/taxonomy/families`, { headers: h }),
        axios.get(`${API}/admin/taxonomy/roles`, { headers: h }),
      ]);
      setTaxonomy(taxRes.data.items || []);
      setTaxonomyTotal(taxRes.data.total || 0);
      setTaxonomyFamilies(famRes.data.families || []);
      setTaxonomyRoles(roleRes.data.roles || []);
    } catch {
      toast.error("Failed to load taxonomy");
    } finally {
      setTaxonomyLoading(false);
    }
  }, [taxonomyFilter]);

  const handleTaxonomyImport = async () => {
    if (!taxonomyImportFile) return;
    setTaxonomyImporting(true);
    const form = new FormData();
    form.append("file", taxonomyImportFile);
    try {
      const res = await axios.post(`${API}/admin/taxonomy/import?mode=${taxonomyImportMode}`, form, { headers: { ...getAuth(), "Content-Type": "multipart/form-data" } });
      toast.success(`Taxonomy imported: ${res.data.inserted} inserted, ${res.data.updated} updated`);
      setTaxonomyImportFile(null);
      loadTaxonomy();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setTaxonomyImporting(false);
    }
  };

  const handleTaxonomyRefresh = async () => {
    setTaxonomyImporting(true);
    try {
      const res = await axios.post(`${API}/admin/taxonomy/refresh?mode=upsert`, {}, { headers: getAuth() });
      toast.success(`Taxonomy refreshed: ${res.data.inserted} inserted, ${res.data.updated} updated`);
      loadTaxonomy();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Refresh failed");
    } finally {
      setTaxonomyImporting(false);
    }
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const h = getAuth();
      const [connRes, stagRes, archRes, clRes, slRes, srRes, dropRes, bulkHistRes] = await Promise.all([
        axios.get(`${API}/admin/connectors/status`, { headers: h }),
        axios.get(`${API}/admin/staging`, { headers: h }),
        axios.get(`${API}/admin/staging/archive-candidates`, { headers: h }),
        axios.get(`${API}/admin/change-logs?limit=50`, { headers: h }),
        axios.get(`${API}/admin/connectors/sync-logs?limit=20`, { headers: h }),
        axios.get(`${API}/admin/sync-runs?limit=30`, { headers: h }),
        axios.get(`${API}/config/dropdowns`),
        axios.get(`${API}/admin/staging/bulk-history?limit=15`, { headers: h }),
      ]);
      setConnectors(connRes.data);
      setStaging(stagRes.data);
      setArchiveCandidates(archRes.data);
      setChangeLogs(clRes.data);
      setSyncLogs(slRes.data);
      setSyncRuns(srRes.data);
      setBulkHistory(bulkHistRes.data || []);
      if (dropRes.data?.hero_products) {
        setHeroProductOptions(dropRes.data.hero_products);
      }
    } catch (err) {
      toast.error("Failed to load connector data");
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleAction = async (resourceId, action, edits) => {
    setActionLoading(prev => ({ ...prev, [resourceId]: true }));
    try {
      await axios.post(`${API}/admin/staging/${resourceId}`, { action, edits }, { headers: getAuth() });
      const label = { approve: "published", reject: "rejected", archive: "archived", restore: "restored", submit_for_review: "submitted for admin review" }[action] || action;
      toast.success(`Resource ${label}`);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setActionLoading(prev => ({ ...prev, [resourceId]: false }));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.size === 0) return;
    try {
      await axios.post(`${API}/admin/staging/bulk`, { resource_ids: Array.from(selectedIds), action }, { headers: getAuth() });
      const label = { approve: "published", reject: "rejected", archive: "archived", submit_for_review: "submitted for admin review" }[action] || action;
      toast.success(`${selectedIds.size} resources ${label}`);
      setSelectedIds(new Set());
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk action failed");
    }
  };

  const handleUndoBulkAction = async (bulkActionId) => {
    try {
      const res = await axios.post(`${API}/admin/staging/bulk-history/${bulkActionId}/undo`, {}, { headers: getAuth() });
      toast.success(`Restored ${res.data.restored} resource(s)`);
      setConfirmUndoId(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Undo failed");
      setConfirmUndoId(null);
    }
  };

  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  // External CSV upload state
  const [extUploadFile, setExtUploadFile] = useState(null);
  const [extUploadLoading, setExtUploadLoading] = useState(false);
  const [extUploadResult, setExtUploadResult] = useState(null);

  // Danger Zone state
  const [showPurgeDialog, setShowPurgeDialog] = useState(false);
  const [purgeConfirmText, setPurgeConfirmText] = useState("");
  const [purging, setPurging] = useState(false);
  const PURGE_TOKEN = "PURGE_ALL_RESOURCES";

  const handleCSVUpload = async () => {
    if (!uploadFile) return;
    setUploadLoading(true);
    setUploadResult(null);
    const form = new FormData();
    form.append("file", uploadFile);
    try {
      // Long uploads: top up the session first so a 72h-old token can't lapse mid-transfer
      await ensureFreshToken({ force: true });
      const res = await axios.post(`${API}/admin/resources/ingest`, form, {
        headers: { ...getAuth(), "Content-Type": "multipart/form-data" },
      });
      setUploadResult(res.data);
      toast.success(`Upload complete: ${res.data.staged} staged, ${res.data.skipped} skipped`);
      setUploadFile(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadLoading(false);
    }
  };

  const handleExternalCSVUpload = async () => {
    if (!extUploadFile) return;
    setExtUploadLoading(true);
    setExtUploadResult(null);
    const form = new FormData();
    form.append("file", extUploadFile);
    try {
      await ensureFreshToken({ force: true });
      const res = await axios.post(`${API}/admin/resources/ingest-external`, form, {
        headers: { ...getAuth(), "Content-Type": "multipart/form-data" },
      });
      setExtUploadResult(res.data);
      toast.success(res.data.message || "External resources ingested");
      setExtUploadFile(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "External upload failed");
    } finally {
      setExtUploadLoading(false);
    }
  };

  const handlePurgeAll = async () => {
    if (purgeConfirmText !== PURGE_TOKEN) return;
    setPurging(true);
    try {
      const res = await axios.post(
        `${API}/admin/resources/purge-all?confirm=${PURGE_TOKEN}`,
        {},
        { headers: getAuth() }
      );
      toast.success(`${res.data.deleted} resources purged. Database is now clean.`);
      setShowPurgeDialog(false);
      setPurgeConfirmText("");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Purge failed");
    } finally {
      setPurging(false);
    }
  };

  const handleLinkCheck = async () => {
    setLinkChecking(true);
    try {
      // POST returns immediately — actual check runs in background
      const startRes = await axios.post(`${API}/admin/connectors/link-check`, {}, { headers: getAuth() });
      if (startRes.data?.status === "already_running") {
        toast.info("A link health check is already in progress. Waiting for results…");
      } else {
        toast.info("Link health check started. Checking URLs in the background…");
      }

      // Poll GET /admin/connectors/link-check every 4 seconds until done
      const poll = async () => {
        try {
          const res = await axios.get(`${API}/admin/connectors/link-check`, { headers: getAuth() });
          const data = res.data;
          if (data.status === "done") {
            setLinkCheckResults({
              total_checked: data.total,
              healthy: (data.total || 0) - (data.broken || 0),
              broken: data.broken || 0,
              broken_resources: (data.details || []).filter(d => d.status !== "ok"),
            });
            toast.success(`Link check complete: ${(data.total || 0) - (data.broken || 0)} healthy, ${data.broken || 0} broken`);
            setLinkChecking(false);
          } else if (data.status === "error") {
            toast.error("Link check encountered an error. Please try again.");
            setLinkChecking(false);
          } else {
            // Still running — poll again
            setTimeout(poll, 4000);
          }
        } catch {
          toast.error("Could not retrieve link check results");
          setLinkChecking(false);
        }
      };
      setTimeout(poll, 4000);
    } catch {
      toast.error("Failed to start link health check");
      setLinkChecking(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedResource) return;
    try {
      // Build payload — convert ai_tags_flat back into a structured ai_tags object
      const { ai_tags_flat, ai_tags, ...rest } = editFields;
      const payload = { ...rest };
      if (typeof ai_tags_flat === "string") {
        const tokens = ai_tags_flat
          .split(/[,\n]/)
          .map(t => t.trim())
          .filter(Boolean);
        payload.ai_tags = {
          ...(ai_tags || {}),
          // Update flat_tags + technology_tags for recommendation propagation
          flat_tags: tokens.join(", "),
          technology_tags: tokens,
          edited_at: new Date().toISOString(),
        };
        payload.ai_tagged = tokens.length > 0;
      }
      await axios.put(`${API}/admin/staging/${selectedResource.resource_id}`, payload, { headers: getAuth() });
      toast.success("Resource updated");
      setEditMode(false);
      setSelectedResource(null);
      loadAll();
    } catch {
      toast.error("Update failed");
    }
  };

  const toggleSelect = (rid) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(rid) ? next.delete(rid) : next.add(rid);
      return next;
    });
  };

  const toggleSelectAll = (list) => {
    if (selectedIds.size === list.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(list.map(r => r.resource_id)));
    }
  };

  const filteredStaging = staging.filter(r => {
    if (stagingFilter === "all") return true;
    if (stagingFilter === "new") return r.change_type === "new";
    if (stagingFilter === "updated") return r.change_type === "updated";
    if (stagingFilter === "inactive") return r.status === "inactive_pending_review";
    if (stagingFilter === "draft") return r.publish_state === "draft" || (r.status === "Staging" && !r.publish_state);
    if (stagingFilter === "pending_review") return r.publish_state === "pending_review";
    return true;
  });

  // Phase 2 May 2026: split staging into 2 tiers (Contributor draft vs. Admin Review)
  const draftCount = staging.filter(r => (r.publish_state === "draft" || (r.status === "Staging" && !r.publish_state))).length;
  const pendingReviewCount = staging.filter(r => r.publish_state === "pending_review").length;

  if (loading && initialLoad) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#194449]" /></div>;
  }

  const totalArchive = archiveCandidates.length;
  const totalInactive = staging.filter(r => r.status === "inactive_pending_review").length;

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === "taxonomy" && taxonomy.length === 0) loadTaxonomy(); }}>
        <TabsList className="bg-white border border-black/5 p-1 rounded-lg mb-6 flex-wrap">
          <TabsTrigger value="connectors" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md text-xs" data-testid="connectors-tab-connectors">
            <Globe className="w-3.5 h-3.5 mr-1.5" /> Connectors
          </TabsTrigger>
          <TabsTrigger value="staging" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md text-xs" data-testid="connectors-tab-staging">
            <Shield className="w-3.5 h-3.5 mr-1.5" /> Staging ({staging.length})
            {totalInactive > 0 && <span className="ml-1 bg-orange-400 text-white text-[10px] px-1.5 rounded-full">{totalInactive}</span>}
          </TabsTrigger>
          {totalArchive > 0 && (
            <TabsTrigger value="archive" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md text-xs" data-testid="connectors-tab-archive">
              <Archive className="w-3.5 h-3.5 mr-1.5" /> Archive Candidates ({totalArchive})
            </TabsTrigger>
          )}
          <TabsTrigger value="sync-runs" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md text-xs" data-testid="connectors-tab-sync-runs">
            <ArrowUpCircle className="w-3.5 h-3.5 mr-1.5" /> Sync Runs
          </TabsTrigger>
          <TabsTrigger value="health" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md text-xs" data-testid="connectors-tab-health">
            <Link2 className="w-3.5 h-3.5 mr-1.5" /> Link Health
          </TabsTrigger>
          <TabsTrigger value="changelog" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md text-xs" data-testid="connectors-tab-changelog">
            <Clock className="w-3.5 h-3.5 mr-1.5" /> Change Log
          </TabsTrigger>
          <TabsTrigger value="taxonomy" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md text-xs" data-testid="connectors-tab-taxonomy">
            <Tag className="w-3.5 h-3.5 mr-1.5" /> Taxonomy
          </TabsTrigger>
        </TabsList>

        {/* ── Connectors Tab ── */}
        <TabsContent value="connectors">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-[#64748B]">Sync resources from external sources. Each run uses hash-based diff detection — only new and changed items are staged for review.</p>
            <Button
              size="sm"
              variant="outline"
              onClick={handleTestDigest}
              disabled={testDigestLoading}
              className="border-[#194449]/20 text-[#194449] hover:bg-[#194449]/5 rounded-full text-xs flex-shrink-0"
              data-testid="test-digest-btn"
              title="Send a preview digest email to all admins"
            >
              {testDigestLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Mail className="w-3.5 h-3.5 mr-1.5" />}
              Test Digest Email
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {connectors.map(c => (
              <ConnectorCard key={c.connector} connector={c} onSync={loadAll} />
            ))}
            {/* Upload CSV Card */}
            <div className="bg-white rounded-xl border border-black/5 shadow-sm p-5 flex flex-col gap-3" data-testid="upload-csv-card">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-[#091F2C] text-sm">Upload CSV</h4>
                  <p className="text-[11px] text-[#64748B] mt-0.5">Stage resources from a LevelUp or custom CSV file</p>
                </div>
                <Badge className="bg-[#F4F3F5] text-[#64748B] border-0 text-[10px]">Manual</Badge>
              </div>

              <div className="border-2 border-dashed border-black/10 rounded-lg p-3 text-center hover:border-[#194449]/30 transition-colors">
                <label className="cursor-pointer block">
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    className="hidden"
                    data-testid="csv-upload-input"
                    onChange={e => { setUploadFile(e.target.files[0]); setUploadResult(null); }}
                  />
                  {uploadFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <Check className="w-3.5 h-3.5 text-[#07641D]" />
                      <span className="text-xs text-[#091F2C] font-medium truncate max-w-[160px]">{uploadFile.name}</span>
                      <button onClick={e => { e.preventDefault(); setUploadFile(null); setUploadResult(null); }} className="text-[#64748B] hover:text-red-500 ml-1">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-1">
                      <Upload className="w-4 h-4 text-[#64748B]" />
                      <span className="text-[11px] text-[#64748B]">Click to select CSV / XLSX</span>
                    </div>
                  )}
                </label>
              </div>

              {uploadResult && (
                <div className="bg-[#f0fdf4] border border-[#07641D]/20 rounded-lg p-2 text-[11px] text-[#07641D]">
                  ✓ {uploadResult.staged} staged · {uploadResult.skipped} skipped
                  {uploadResult.errors?.length > 0 && (
                    <span className="text-orange-600 ml-1">· {uploadResult.errors.length} errors</span>
                  )}
                </div>
              )}

              <p className="text-[10px] text-[#64748B] leading-relaxed">
                Supports columns: <code className="bg-black/5 px-1 rounded">course_url</code>, <code className="bg-black/5 px-1 rounded">course_title</code>, <code className="bg-black/5 px-1 rounded">course_id</code>. Resources go to Staging for review.
              </p>

              <Button
                size="sm"
                onClick={handleCSVUpload}
                disabled={!uploadFile || uploadLoading}
                className="bg-[#194449] hover:bg-[#194449]/90 text-white rounded-full text-xs w-full"
                data-testid="csv-upload-btn"
              >
                {uploadLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                {uploadLoading ? "Uploading..." : "Upload & Stage"}
              </Button>
            </div>

            {/* External Resource CSV Upload Card */}
            <div className="bg-white rounded-xl border border-black/5 shadow-sm p-5 flex flex-col gap-3" data-testid="external-csv-upload-card">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-[#091F2C] text-sm">External Resources CSV</h4>
                  <p className="text-[11px] text-[#64748B] mt-0.5">Upload MS Learn, guides, playbooks, toolkits using the structured template</p>
                </div>
                <Badge className="bg-purple-100 text-purple-800 border-0 text-[10px]">External</Badge>
              </div>

              <div className="border-2 border-dashed border-black/10 rounded-lg p-3 text-center hover:border-purple-400/40 transition-colors">
                <label className="cursor-pointer block">
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    className="hidden"
                    data-testid="external-csv-upload-input"
                    onChange={e => { setExtUploadFile(e.target.files[0]); setExtUploadResult(null); }}
                  />
                  {extUploadFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <Check className="w-3.5 h-3.5 text-purple-600" />
                      <span className="text-xs text-[#091F2C] font-medium truncate max-w-[160px]">{extUploadFile.name}</span>
                      <button onClick={e => { e.preventDefault(); setExtUploadFile(null); setExtUploadResult(null); }} className="text-[#64748B] hover:text-red-500 ml-1">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-1">
                      <Upload className="w-4 h-4 text-purple-500" />
                      <span className="text-[11px] text-[#64748B]">Select External Resources CSV / XLSX</span>
                    </div>
                  )}
                </label>
              </div>

              {extUploadResult && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-2 text-[11px] text-purple-800">
                  {extUploadResult.staged} staged · {extUploadResult.auto_published} auto-published · {extUploadResult.skipped} skipped
                  {extUploadResult.errors?.length > 0 && (
                    <span className="text-orange-600 ml-1">· {extUploadResult.errors.length} errors</span>
                  )}
                  {extUploadResult.warnings?.length > 0 && (
                    <div className="text-[10px] text-[#64748B] mt-1">{extUploadResult.warnings.join("; ")}</div>
                  )}
                </div>
              )}

              <p className="text-[10px] text-[#64748B] leading-relaxed">
                Uses structured template: <code className="bg-black/5 px-1 rounded">resource_title</code>, <code className="bg-black/5 px-1 rounded">resource_url</code>, <code className="bg-black/5 px-1 rounded">intended_roles</code>, <code className="bg-black/5 px-1 rounded">primary_products</code>.
                Supports <code className="bg-black/5 px-1 rounded">stage_only</code> and <code className="bg-black/5 px-1 rounded">stage_or_auto_if_high_confidence</code> modes.
              </p>

              <Button
                size="sm"
                onClick={handleExternalCSVUpload}
                disabled={!extUploadFile || extUploadLoading}
                className="bg-purple-600 hover:bg-purple-700 text-white rounded-full text-xs w-full"
                data-testid="external-csv-upload-btn"
              >
                {extUploadLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                {extUploadLoading ? "Ingesting..." : "Upload External Resources"}
              </Button>
            </div>
          </div>
          {/* Legacy Sync History */}
          <div className="bg-white rounded-xl border border-black/5 shadow-sm">
            <div className="p-5 border-b border-black/5">
              <h3 className="font-medium text-[#091F2C] text-sm">Recent Sync History</h3>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Connector</TableHead>
                    <TableHead className="text-xs">Scraped</TableHead>
                    <TableHead className="text-xs">New</TableHead>
                    <TableHead className="text-xs">Duplicates</TableHead>
                    <TableHead className="text-xs">Triggered By</TableHead>
                    <TableHead className="text-xs">Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {syncLogs.map((s, i) => (
                    <TableRow key={i}>
                      <TableCell><Badge variant="outline" className="text-xs">{s.connector}</Badge></TableCell>
                      <TableCell className="text-xs">{s.scraped_count}</TableCell>
                      <TableCell className="text-xs font-medium">{s.new_count}</TableCell>
                      <TableCell className="text-xs">{s.duplicate_count || 0}</TableCell>
                      <TableCell className="text-xs">{s.triggered_by}</TableCell>
                      <TableCell className="text-xs">{new Date(s.timestamp).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                  {syncLogs.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-sm text-[#64748B] py-8">No syncs yet. Trigger a connector above.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* ── Danger Zone ── */}
          <div className="mt-8 border border-red-200 rounded-xl overflow-hidden" data-testid="danger-zone">
            <div className="bg-red-50 px-5 py-3 flex items-center gap-2 border-b border-red-200">
              <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <span className="font-semibold text-red-700 text-sm">Danger Zone</span>
            </div>
            <div className="bg-white px-5 py-4 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-[#091F2C]">Purge All Resources</p>
                <p className="text-xs text-[#64748B] mt-0.5">
                  Permanently delete every resource (Active + Staging) from the database. Use this before ingesting a fresh real-data CSV. This action cannot be undone.
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setShowPurgeDialog(true); setPurgeConfirmText(""); }}
                className="border-red-300 text-red-600 hover:bg-red-50 rounded-full text-xs flex-shrink-0"
                data-testid="purge-all-btn"
              >
                <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
                Purge All Resources
              </Button>
            </div>
          </div>

          {/* ── Purge Confirmation Dialog ── */}
          {showPurgeDialog && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="purge-dialog">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
                <div className="bg-red-600 px-6 py-4 flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-white" />
                  <h3 className="text-white font-bold text-base">Confirm Resource Purge</h3>
                </div>
                <div className="p-6 space-y-4">
                  <p className="text-sm text-[#091F2C]">
                    This will <strong>permanently delete all resources</strong> from the database — including Active, Staging, and archived entries. This cannot be undone.
                  </p>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">
                    <strong>What gets deleted:</strong> All resources (Active + Staging), change logs<br/>
                    <strong>What is kept:</strong> Admin users, sessions, taxonomy, config, sync history
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#64748B] block mb-1.5">
                      Type <code className="bg-black/5 px-1.5 py-0.5 rounded font-mono text-red-600">{PURGE_TOKEN}</code> to confirm:
                    </label>
                    <input
                      type="text"
                      value={purgeConfirmText}
                      onChange={e => setPurgeConfirmText(e.target.value)}
                      placeholder={PURGE_TOKEN}
                      className="w-full border border-red-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-400"
                      data-testid="purge-confirm-input"
                      autoFocus
                    />
                  </div>
                  <div className="flex gap-3 pt-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setShowPurgeDialog(false); setPurgeConfirmText(""); }}
                      className="flex-1 rounded-full"
                      data-testid="purge-cancel-btn"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handlePurgeAll}
                      disabled={purgeConfirmText !== PURGE_TOKEN || purging}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full"
                      data-testid="purge-confirm-btn"
                    >
                      {purging ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />}
                      {purging ? "Purging..." : "Yes, Purge Everything"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ── Staging Tab ── */}
        <TabsContent value="staging">
          <div className="bg-white rounded-xl border border-black/5 shadow-sm">
            <div className="p-5 border-b border-black/5 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <h3 className="font-medium text-[#091F2C] text-sm">Staging Queue ({filteredStaging.length})</h3>
                <Select value={stagingFilter} onValueChange={setStagingFilter}>
                  <SelectTrigger className="h-7 text-xs w-36 border-black/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All ({staging.length})</SelectItem>
                    <SelectItem value="draft">Draft / Contributor ({draftCount})</SelectItem>
                    <SelectItem value="pending_review">Pending Admin Review ({pendingReviewCount})</SelectItem>
                    <SelectItem value="new">New only</SelectItem>
                    <SelectItem value="updated">Updated only</SelectItem>
                    {totalInactive > 0 && <SelectItem value="inactive">Inactive ({totalInactive})</SelectItem>}
                  </SelectContent>
                </Select>
                {staging.length > 0 && (
                  <span className="text-[10px] text-[#64748B] flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Digest sent on last sync
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                {selectedIds.size > 0 && (
                  <>
                    <Button size="sm" onClick={() => handleBulkAction("submit_for_review")} className="bg-[#0E7490] text-white hover:bg-[#0E7490]/90 rounded-full text-xs" data-testid="bulk-submit-for-review-btn" title="Mark as ready for admin review (two-stage workflow)">
                      <ArrowUpCircle className="w-3.5 h-3.5 mr-1" /> Submit for Review ({selectedIds.size})
                    </Button>
                    <Button size="sm" onClick={() => handleBulkAction("approve")} className="bg-[#07641D] text-white hover:bg-[#07641D]/90 rounded-full text-xs" data-testid="bulk-approve-btn">
                      <Check className="w-3.5 h-3.5 mr-1" /> Approve ({selectedIds.size})
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleBulkAction("reject")} className="rounded-full text-xs" data-testid="bulk-reject-btn">
                      <X className="w-3.5 h-3.5 mr-1" /> Reject ({selectedIds.size})
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleBulkAction("archive")} className="rounded-full text-xs text-gray-600" data-testid="bulk-archive-btn">
                      <Archive className="w-3.5 h-3.5 mr-1" /> Archive ({selectedIds.size})
                    </Button>
                  </>
                )}
                <Button size="sm" variant="ghost" onClick={loadAll}><RefreshCw className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <input type="checkbox" checked={selectedIds.size === filteredStaging.length && filteredStaging.length > 0} onChange={() => toggleSelectAll(filteredStaging)} className="rounded" data-testid="select-all-staging" />
                    </TableHead>
                    <TableHead className="text-xs">Resource</TableHead>
                    <TableHead className="text-xs">Change</TableHead>
                    <TableHead className="text-xs">Source</TableHead>
                    <TableHead className="text-xs">Solution Area</TableHead>
                    <TableHead className="text-xs">Hero Products</TableHead>
                    <TableHead className="text-xs">AI Tags</TableHead>
                    <TableHead className="text-xs">Confidence</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStaging.map((r, i) => (
                    <TableRow key={i} className="group">
                      <TableCell>
                        <input type="checkbox" checked={selectedIds.has(r.resource_id)} onChange={() => toggleSelect(r.resource_id)} className="rounded" data-testid={`staging-select-${r.resource_id}`} />
                      </TableCell>
                      <TableCell>
                        <div className="max-w-[240px]">
                          <div className="text-xs font-medium text-[#091F2C] truncate">{r.title}</div>
                          <div className="text-[10px] text-[#64748B] truncate mt-0.5">{r.staging_reason || r.source_connector}</div>
                          {r.change_type === "updated" && <FieldDiffPopover fieldDiffs={r.field_diffs} />}
                        </div>
                      </TableCell>
                      <TableCell><ChangeTypeBadge type={r.change_type} /></TableCell>
                      <TableCell><Badge variant="secondary" className="text-[10px]">{r.provider_name || r.source_connector || r.resource_type}</Badge></TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1 max-w-[160px]">
                          {r.solution_area ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#B9DCD2]/40 text-[#091F2C] inline-block w-fit">
                              {r.solution_area.replace(/_/g, " ")}
                            </span>
                          ) : (
                            <span className="text-[10px] text-[#94A3B8] italic">—</span>
                          )}
                          {(r.role_tags || []).length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {(r.role_tags || []).slice(0, 3).map(t => (
                                <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-[#194449]/10 text-[#194449]">{t}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-[130px]">
                          {(r.hero_products || []).slice(0, 2).map(p => (
                            <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-[#194449]/20 text-[#194449]">{p.replace(/_/g, " ")}</span>
                          ))}
                          {(r.hero_products || []).length > 2 && <span className="text-[10px] text-[#64748B]">+{r.hero_products.length - 2}</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-[200px]" data-testid={`staging-ai-tags-${r.resource_id}`}>
                          {r.ai_tagged && r.ai_tags?.flat_tags ? (
                            <>
                              {r.ai_tags.flat_tags.split(", ").slice(0, 3).map((tag, ti) => (
                                <span key={ti} className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#194449]/10 text-[#194449] whitespace-nowrap">{tag}</span>
                              ))}
                              {r.ai_tags.flat_tags.split(", ").length > 3 && (
                                <span className="text-[9px] text-[#64748B]">+{r.ai_tags.flat_tags.split(", ").length - 3}</span>
                              )}
                            </>
                          ) : (
                            <span className="text-[10px] text-[#94A3B8] italic">Tagging in progress…</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell><ConfidenceBadge score={r.ai_confidence_score || 0} /></TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <StatusBadge status={r.status} />
                          {r.publish_state === "pending_review" && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#0E7490]/15 text-[#0E7490] font-medium whitespace-nowrap" data-testid={`tier-badge-pending-${r.resource_id}`}>
                              Pending Admin Review
                            </span>
                          )}
                          {(r.publish_state === "draft" || (!r.publish_state && r.status === "Staging")) && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium whitespace-nowrap" data-testid={`tier-badge-draft-${r.resource_id}`}>
                              Contributor Draft
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {r.publish_state !== "pending_review" && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-[#0E7490] hover:bg-[#0E7490]/10" onClick={() => handleAction(r.resource_id, "submit_for_review")} disabled={actionLoading[r.resource_id]} data-testid={`submit-review-${i}`} title="Submit for Admin Review">
                              <ArrowUpCircle className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-[#07641D] hover:bg-[#D4EC8E]/30" onClick={() => handleAction(r.resource_id, "approve")} disabled={actionLoading[r.resource_id]} data-testid={`approve-${i}`} title="Approve & Publish">
                            {actionLoading[r.resource_id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" onClick={() => handleAction(r.resource_id, "reject")} disabled={actionLoading[r.resource_id]} data-testid={`reject-${i}`} title="Reject">
                            <X className="w-3.5 h-3.5" />
                          </Button>
                          {r.status === "inactive_pending_review" && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-[#194449] hover:bg-[#B9DCD2]/30" onClick={() => handleAction(r.resource_id, "restore")} disabled={actionLoading[r.resource_id]} data-testid={`restore-${i}`} title="Restore to Active">
                              <RotateCcw className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:bg-gray-100" onClick={() => { setSelectedResource(r); setEditMode(true); setEditFields({ title: r.title, role_tags: r.role_tags, solution_area: r.solution_area, skill_level: r.skill_level, topic_tags: r.topic_tags || [], hero_products: r.hero_products || [], product_coverage: r.product_coverage || 0, ai_tags: r.ai_tags || {}, ai_tags_flat: r.ai_tags?.flat_tags || "" }); }} data-testid={`edit-${i}`} title="Edit">
                            <Edit3 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredStaging.length === 0 && (
                    <TableRow><TableCell colSpan={10} className="text-center text-sm text-[#64748B] py-8">No items in staging. Run a connector sync to populate.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* ── Recent Bulk Actions (Undo, Aug 2026) ── */}
          <div className="bg-white rounded-xl border border-black/5 shadow-sm mt-4">
            <div className="p-5 border-b border-black/5">
              <h3 className="font-medium text-[#091F2C] text-sm">Recent Bulk Actions</h3>
              <p className="text-xs text-[#64748B] mt-0.5">Approve, reject, and archive bulk actions can be undone within 24 hours.</p>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Action</TableHead>
                    <TableHead className="text-xs">Resources</TableHead>
                    <TableHead className="text-xs">Performed By</TableHead>
                    <TableHead className="text-xs">When</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">Undo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bulkHistory.map((b) => (
                    <TableRow key={b.bulk_action_id} data-testid={`bulk-history-row-${b.bulk_action_id}`}>
                      <TableCell className="text-xs capitalize font-medium">{b.action}</TableCell>
                      <TableCell className="text-xs">{b.resource_count}</TableCell>
                      <TableCell className="text-xs text-[#64748B]">{b.performed_by || "—"}</TableCell>
                      <TableCell className="text-xs text-[#64748B]">{b.created_at ? new Date(b.created_at).toLocaleString() : "—"}</TableCell>
                      <TableCell className="text-xs">
                        {b.undone ? (
                          <Badge variant="secondary" className="text-[10px]">Undone</Badge>
                        ) : b.can_undo ? (
                          <Badge className="bg-[#D4EC8E] text-[#091F2C] border-0 text-[10px]">Active</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px] text-[#94A3B8]">Expired</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm" variant="outline" disabled={!b.can_undo}
                          className="h-7 text-xs gap-1 text-[#194449] border-[#194449]/30 hover:bg-[#194449]/5 disabled:opacity-40"
                          onClick={() => setConfirmUndoId(b.bulk_action_id)}
                          data-testid={`undo-bulk-${b.bulk_action_id}`}
                        >
                          <RotateCcw className="w-3 h-3" /> Undo
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {bulkHistory.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-sm text-[#64748B] py-6">No bulk actions in the last 24 hours.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Undo Confirmation Dialog */}
          <AlertDialog open={!!confirmUndoId} onOpenChange={(open) => !open && setConfirmUndoId(null)}>
            <AlertDialogContent data-testid="undo-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Undo this bulk action?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will restore the affected resources to their exact state before the action was applied. This cannot be undone again.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="undo-confirm-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="undo-confirm-proceed" onClick={() => handleUndoBulkAction(confirmUndoId)}>
                  Undo
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Edit Dialog */}
          <Dialog open={editMode} onOpenChange={setEditMode}>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" aria-describedby="edit-resource-description">
              <DialogHeader>
                <DialogTitle>Edit Resource</DialogTitle>
                <p id="edit-resource-description" className="text-xs text-[#64748B]">Update resource metadata before publishing</p>
              </DialogHeader>
              {selectedResource && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs">Title</Label>
                    <Input value={editFields.title || ""} onChange={e => setEditFields(p => ({ ...p, title: e.target.value }))} className="h-9 text-sm" data-testid="edit-title-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Solution Area</Label>
                      <Select value={editFields.solution_area || ""} onValueChange={v => setEditFields(p => ({ ...p, solution_area: v }))}>
                        <SelectTrigger className="h-9 text-xs" data-testid="edit-solution-area">
                          <SelectValue placeholder="Select area..." />
                        </SelectTrigger>
                        <SelectContent>
                          {[
                            { id: "cloud_ai_platform", label: "Cloud & AI Platform" },
                            { id: "security", label: "Security" },
                            { id: "ai_workforce", label: "AI Workforce" },
                            { id: "ai_business_process", label: "AI Business Process" },
                          ].map(a => (
                            <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Skill Level</Label>
                      <Select value={editFields.skill_level || ""} onValueChange={v => setEditFields(p => ({ ...p, skill_level: v }))}>
                        <SelectTrigger className="h-9 text-xs" data-testid="edit-skill-level">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {["Beginner", "Intermediate", "Advanced"].map(l => (
                            <SelectItem key={l} value={l}>{l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {/* Role Tags editor — multi-toggle chips */}
                  <div>
                    <Label className="text-xs">Role Tags</Label>
                    <div className="flex gap-2 mt-1.5 flex-wrap">
                      {["Sales/Pre-Sales", "Technical", "GTM"].map(tag => {
                        const active = (editFields.role_tags || []).includes(tag);
                        return (
                          <button
                            key={tag}
                            type="button"
                            data-testid={`edit-role-tag-${tag.replace(/\//g, "-")}`}
                            onClick={() => setEditFields(prev => ({
                              ...prev,
                              role_tags: active
                                ? prev.role_tags.filter(t => t !== tag)
                                : [...(prev.role_tags || []), tag],
                            }))}
                            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                              active
                                ? "bg-[#194449] text-white border-[#194449]"
                                : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#194449]/50"
                            }`}
                          >
                            {tag}
                          </button>
                        );
                      })}
                    </div>
                    {(editFields.role_tags || []).length === 0 && (
                      <p className="text-[10px] text-[#94A3B8] mt-1">No role selected — resource will appear for all roles.</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Hero Products</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1.5 p-2 border rounded-md bg-[#F4F3F5] min-h-[40px]">
                      {(editFields.hero_products || []).map(p => (
                        <Badge key={p} className="text-xs bg-[#194449] text-white border-0 flex items-center gap-1 pr-1">
                          {heroProductOptions.find(h => h.id === p)?.display_name || p.replace(/_/g, " ")}
                          <button type="button" className="ml-1 hover:bg-white/20 rounded-full p-0.5" onClick={() => setEditFields(prev => ({ ...prev, hero_products: prev.hero_products.filter(hp => hp !== p) }))}>
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <Select value="" onValueChange={v => { if (v && !(editFields.hero_products || []).includes(v)) { setEditFields(prev => ({ ...prev, hero_products: [...(prev.hero_products || []), v] })); } }}>
                      <SelectTrigger className="h-8 text-xs mt-1.5" data-testid="edit-hero-products"><SelectValue placeholder="Add a product..." /></SelectTrigger>
                      <SelectContent>
                        {heroProductOptions.filter(h => !(editFields.hero_products || []).includes(h.id)).map(h => (
                          <SelectItem key={h.id} value={h.id}>{h.display_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Product Coverage ({editFields.product_coverage || 0}%)</Label>
                    <div className="flex items-center gap-3 mt-1.5">
                      <input type="range" min="0" max="100" value={editFields.product_coverage || 0} onChange={e => setEditFields(p => ({ ...p, product_coverage: parseInt(e.target.value, 10) }))} className="flex-1 h-2 bg-[#F4F3F5] rounded-lg appearance-none cursor-pointer accent-[#194449]" data-testid="edit-product-coverage" />
                      <Input type="number" min="0" max="100" value={editFields.product_coverage || 0} onChange={e => setEditFields(p => ({ ...p, product_coverage: Math.min(100, Math.max(0, parseInt(e.target.value, 10) || 0)) }))} className="w-16 h-8 text-xs text-center" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">AI Tags (comma-separated; edit to refine)</Label>
                    <Textarea
                      value={editFields.ai_tags_flat || ""}
                      onChange={e => setEditFields(p => ({ ...p, ai_tags_flat: e.target.value }))}
                      placeholder="e.g. Microsoft Sentinel, Data Loss Prevention, Level 200, SIEM"
                      className="text-xs min-h-[70px] mt-1"
                      data-testid="edit-ai-tags-input"
                    />
                    <p className="text-[10px] text-[#64748B] mt-1">
                      Pulled from auto-tagging. Edits update <code>ai_tags.flat_tags</code> and propagate to recommendations.
                    </p>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button onClick={handleSaveEdit} className="bg-[#194449] text-white hover:bg-[#07641D] rounded-full flex-1 text-sm" data-testid="save-edit-btn">Save Changes</Button>
                    <Button onClick={() => { handleAction(selectedResource.resource_id, "approve", editFields); setEditMode(false); }} className="bg-[#07641D] text-white hover:bg-[#07641D]/90 rounded-full flex-1 text-sm" data-testid="save-and-approve-btn">
                      Save & Publish
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ── Archive Candidates Tab ── */}
        <TabsContent value="archive">
          <div className="bg-white rounded-xl border border-black/5 shadow-sm">
            <div className="p-5 border-b border-black/5">
              <h3 className="font-medium text-[#091F2C] text-sm">Archive Candidates ({archiveCandidates.length})</h3>
              <p className="text-xs text-[#64748B] mt-1">Resources not found in 3+ consecutive sync runs. Review before archiving.</p>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Resource</TableHead>
                    <TableHead className="text-xs">Source</TableHead>
                    <TableHead className="text-xs">Missed Runs</TableHead>
                    <TableHead className="text-xs">Reason</TableHead>
                    <TableHead className="text-xs">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {archiveCandidates.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <div className="max-w-[250px]">
                          <div className="text-xs font-medium truncate">{r.title}</div>
                          <div className="text-[10px] text-[#64748B] font-mono truncate">{r.resource_id}</div>
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="secondary" className="text-[10px]">{r.source_connector}</Badge></TableCell>
                      <TableCell>
                        <span className={`text-xs font-medium ${r.missing_run_count >= 5 ? "text-red-600" : "text-orange-600"}`}>
                          {r.missing_run_count} runs
                        </span>
                      </TableCell>
                      <TableCell className="text-xs max-w-[200px] truncate text-[#64748B]">{r.staging_reason}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-[#194449] hover:bg-[#B9DCD2]/30 text-xs" onClick={() => handleAction(r.resource_id, "restore")} disabled={actionLoading[r.resource_id]} data-testid={`restore-archive-${i}`}>
                            <RotateCcw className="w-3 h-3 mr-1" /> Keep
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-gray-500 hover:bg-gray-100 text-xs" onClick={() => handleAction(r.resource_id, "archive")} disabled={actionLoading[r.resource_id]} data-testid={`archive-confirm-${i}`}>
                            <Archive className="w-3 h-3 mr-1" /> Archive
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {archiveCandidates.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-sm text-[#64748B] py-8">No archive candidates. All resources are current.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        {/* ── Sync Runs Tab ── */}
        <TabsContent value="sync-runs">
          <div className="bg-white rounded-xl border border-black/5 shadow-sm">
            <div className="p-5 border-b border-black/5 flex items-center justify-between">
              <h3 className="font-medium text-[#091F2C] text-sm">Sync Run History ({syncRuns.length})</h3>
              <Button size="sm" variant="ghost" onClick={loadAll}><RefreshCw className="w-3.5 h-3.5" /></Button>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Connector</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">Fetched</TableHead>
                    <TableHead className="text-xs">New</TableHead>
                    <TableHead className="text-xs">Updated</TableHead>
                    <TableHead className="text-xs">Unchanged</TableHead>
                    <TableHead className="text-xs">Missing</TableHead>
                    <TableHead className="text-xs">Duration</TableHead>
                    <TableHead className="text-xs">Triggered By</TableHead>
                    <TableHead className="text-xs">Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {syncRuns.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell><Badge variant="outline" className="text-xs">{CONNECTOR_LABELS[r.connector] || r.connector}</Badge></TableCell>
                      <TableCell>
                        <Badge className={`text-[10px] border-0 ${r.status === "completed" ? "bg-[#D4EC8E] text-[#091F2C]" : r.status === "failed" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-800"}`}>
                          {r.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">{r.records_fetched}</TableCell>
                      <TableCell className="text-xs font-medium text-[#07641D]">{r.records_new}</TableCell>
                      <TableCell className="text-xs font-medium text-blue-700">{r.records_updated}</TableCell>
                      <TableCell className="text-xs text-[#64748B]">{r.records_unchanged}</TableCell>
                      <TableCell className={`text-xs ${r.records_missing > 0 ? "text-orange-600 font-medium" : "text-[#64748B]"}`}>{r.records_missing}</TableCell>
                      <TableCell className="text-xs">{r.duration_seconds != null ? `${r.duration_seconds}s` : "—"}</TableCell>
                      <TableCell className="text-xs">{r.triggered_by}</TableCell>
                      <TableCell className="text-xs">{r.start_time ? new Date(r.start_time).toLocaleString() : "—"}</TableCell>
                    </TableRow>
                  ))}
                  {syncRuns.length === 0 && (
                    <TableRow><TableCell colSpan={10} className="text-center text-sm text-[#64748B] py-8">No sync runs yet. Trigger a connector to create the first run.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        {/* ── Link Health Tab ── */}
        <TabsContent value="health">
          <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium text-[#091F2C]">Link Health Check</h3>
                <p className="text-xs text-[#64748B] mt-1">Verify all published resource URLs are accessible.</p>
              </div>
              <Button onClick={handleLinkCheck} disabled={linkChecking} className="bg-[#194449] text-white hover:bg-[#07641D] rounded-full text-xs" data-testid="run-link-check-btn">
                {linkChecking ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Link2 className="w-3.5 h-3.5 mr-1.5" />}
                {linkChecking ? "Checking…" : "Run Check"}
              </Button>
            </div>
            {linkCheckResults && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-[#F4F3F5] rounded-lg p-4 text-center">
                    <div className="text-2xl font-semibold text-[#091F2C]">{linkCheckResults.total_checked}</div>
                    <div className="text-xs text-[#64748B]">Total Checked</div>
                  </div>
                  <div className="bg-[#D4EC8E]/20 rounded-lg p-4 text-center">
                    <div className="text-2xl font-semibold text-[#07641D]">{linkCheckResults.healthy}</div>
                    <div className="text-xs text-[#64748B]">Healthy</div>
                  </div>
                  <div className={`rounded-lg p-4 text-center ${linkCheckResults.broken > 0 ? "bg-red-50" : "bg-[#F4F3F5]"}`}>
                    <div className={`text-2xl font-semibold ${linkCheckResults.broken > 0 ? "text-red-600" : "text-[#091F2C]"}`}>{linkCheckResults.broken}</div>
                    <div className="text-xs text-[#64748B]">Broken</div>
                  </div>
                </div>
                {linkCheckResults.broken_resources?.length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Resource ID</TableHead>
                        <TableHead className="text-xs">URL</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {linkCheckResults.broken_resources.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs font-mono">{r.resource_id}</TableCell>
                          <TableCell className="text-xs max-w-[300px] truncate">{r.url}</TableCell>
                          <TableCell><Badge variant="destructive" className="text-xs">{r.status_code || "Error"}</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}
            {!linkCheckResults && <p className="text-sm text-[#64748B] text-center py-8">Run a link check to see results.</p>}
          </div>
        </TabsContent>

        {/* ── Change Log Tab ── */}
        <TabsContent value="changelog">
          <div className="bg-white rounded-xl border border-black/5 shadow-sm">
            <div className="p-5 border-b border-black/5">
              <h3 className="font-medium text-[#091F2C] text-sm">Change Log ({changeLogs.length})</h3>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Resource ID</TableHead>
                    <TableHead className="text-xs">Action</TableHead>
                    <TableHead className="text-xs">Details</TableHead>
                    <TableHead className="text-xs">By</TableHead>
                    <TableHead className="text-xs">Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {changeLogs.map((log, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs font-mono max-w-[120px] truncate">{log.resource_id}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs ${log.action === "published" ? "border-[#07641D] text-[#07641D]" : log.action === "rejected" ? "border-red-400 text-red-600" : log.action === "archived" ? "border-gray-400 text-gray-600" : ""}`}>
                          {log.action}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs max-w-[200px] truncate">{JSON.stringify(log.changes)}</TableCell>
                      <TableCell className="text-xs">{log.performed_by}</TableCell>
                      <TableCell className="text-xs">{new Date(log.timestamp).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                  {changeLogs.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-sm text-[#64748B] py-8">No changes recorded yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        {/* ── Taxonomy Tab ── */}
        <TabsContent value="taxonomy">
          <div className="space-y-4">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-semibold text-[#091F2C] text-sm">Technology Taxonomy</h3>
                <p className="text-xs text-[#64748B] mt-0.5">
                  Canonical naming registry — {taxonomyTotal} entries. Manages product renames (e.g. Azure AI Foundry → Microsoft Foundry) and alias resolution.
                </p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button size="sm" variant="outline" onClick={() => loadTaxonomy()} disabled={taxonomyLoading}
                  className="border-[#194449]/20 text-[#194449] hover:bg-[#194449]/5 rounded-full text-xs"
                  data-testid="taxonomy-refresh-btn">
                  {taxonomyLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                  Refresh View
                </Button>
                <Button size="sm" variant="outline" onClick={handleTaxonomyRefresh} disabled={taxonomyImporting}
                  className="border-[#194449]/20 text-[#194449] hover:bg-[#194449]/5 rounded-full text-xs"
                  data-testid="taxonomy-reseed-btn">
                  {taxonomyImporting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />}
                  Re-seed from CSV
                </Button>
              </div>
            </div>

            {/* Filters */}
            <div className="flex gap-2 flex-wrap items-end bg-white border border-black/5 rounded-xl p-3">
              <div className="flex-1 min-w-[150px]">
                <label className="text-[10px] text-[#64748B] font-medium uppercase tracking-wide mb-1 block">Technology Name</label>
                <Input size="sm" placeholder="Search..." value={taxonomyFilter.tech_name}
                  onChange={e => setTaxonomyFilter(f => ({ ...f, tech_name: e.target.value }))}
                  className="h-8 text-xs" data-testid="taxonomy-filter-name" />
              </div>
              <div className="flex-1 min-w-[140px]">
                <label className="text-[10px] text-[#64748B] font-medium uppercase tracking-wide mb-1 block">Product Family</label>
                <Select value={taxonomyFilter.product_family} onValueChange={v => setTaxonomyFilter(f => ({ ...f, product_family: v === "__all__" ? "" : v }))}>
                  <SelectTrigger className="h-8 text-xs" data-testid="taxonomy-filter-family">
                    <SelectValue placeholder="All families" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All families</SelectItem>
                    {taxonomyFamilies.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[160px]">
                <label className="text-[10px] text-[#64748B] font-medium uppercase tracking-wide mb-1 block">Role</label>
                <Select value={taxonomyFilter.role_name} onValueChange={v => setTaxonomyFilter(f => ({ ...f, role_name: v === "__all__" ? "" : v }))}>
                  <SelectTrigger className="h-8 text-xs" data-testid="taxonomy-filter-role">
                    <SelectValue placeholder="All roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All roles</SelectItem>
                    {taxonomyRoles.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[120px]">
                <label className="text-[10px] text-[#64748B] font-medium uppercase tracking-wide mb-1 block">Status</label>
                <Select value={taxonomyFilter.is_active} onValueChange={v => setTaxonomyFilter(f => ({ ...f, is_active: v === "__all__" ? "" : v }))}>
                  <SelectTrigger className="h-8 text-xs" data-testid="taxonomy-filter-status">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All</SelectItem>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button size="sm" onClick={loadTaxonomy} disabled={taxonomyLoading}
                className="bg-[#194449] hover:bg-[#194449]/90 text-white rounded-full h-8 text-xs px-4"
                data-testid="taxonomy-search-btn">
                <Filter className="w-3 h-3 mr-1" /> Filter
              </Button>
            </div>

            {/* Import section */}
            <div className="bg-white border border-black/5 rounded-xl p-3 flex flex-wrap gap-3 items-end">
              <div>
                <label className="text-[10px] text-[#64748B] font-medium uppercase tracking-wide mb-1 block">Import Taxonomy CSV</label>
                <input type="file" accept=".csv" className="text-xs" data-testid="taxonomy-file-input"
                  onChange={e => setTaxonomyImportFile(e.target.files[0])} />
              </div>
              <div>
                <label className="text-[10px] text-[#64748B] font-medium uppercase tracking-wide mb-1 block">Import Mode</label>
                <Select value={taxonomyImportMode} onValueChange={setTaxonomyImportMode}>
                  <SelectTrigger className="h-8 text-xs w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="insert_new">Insert New Only</SelectItem>
                    <SelectItem value="upsert">Upsert (Insert + Update)</SelectItem>
                    <SelectItem value="replace">Replace All</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button size="sm" onClick={handleTaxonomyImport} disabled={!taxonomyImportFile || taxonomyImporting}
                className="bg-[#194449] hover:bg-[#194449]/90 text-white rounded-full h-8 text-xs px-4"
                data-testid="taxonomy-import-btn">
                {taxonomyImporting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                Import
              </Button>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border border-black/5 shadow-sm overflow-x-auto">
              {taxonomyLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#194449]" /></div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">ID</TableHead>
                      <TableHead className="text-xs">Canonical Name</TableHead>
                      <TableHead className="text-xs">Internal Slug</TableHead>
                      <TableHead className="text-xs">Aliases</TableHead>
                      <TableHead className="text-xs">Family</TableHead>
                      <TableHead className="text-xs">Role</TableHead>
                      <TableHead className="text-xs">Priority</TableHead>
                      <TableHead className="text-xs">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {taxonomy.length === 0 && (
                      <TableRow><TableCell colSpan={8} className="text-center text-sm text-[#64748B] py-10">
                        No taxonomy entries found. Click "Refresh View" to load or adjust filters.
                      </TableCell></TableRow>
                    )}
                    {taxonomy.map(t => (
                      <TableRow key={t.technology_id} data-testid={`taxonomy-row-${t.technology_id}`}>
                        <TableCell className="text-xs font-mono text-[#64748B]">{t.technology_id}</TableCell>
                        <TableCell className="text-xs font-medium text-[#091F2C]">
                          {t.technology_name}
                          {t.technology_name !== (t.canonical_slug || "").replace(/_/g, " ") && (
                            <div className="text-[10px] text-[#64748B]">display name</div>
                          )}
                        </TableCell>
                        <TableCell className="text-xs font-mono text-[#194449]">{t.canonical_slug}</TableCell>
                        <TableCell className="text-xs max-w-[200px]">
                          <div className="flex flex-wrap gap-0.5">
                            {(t.aliases || []).slice(0, 3).map(a => (
                              <span key={a} className="bg-blue-50 text-blue-700 text-[10px] px-1.5 py-0.5 rounded">{a}</span>
                            ))}
                            {(t.aliases || []).length > 3 && (
                              <span className="text-[10px] text-[#64748B]">+{t.aliases.length - 3} more</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs">{t.product_family}</TableCell>
                        <TableCell className="text-xs max-w-[140px] truncate">{t.role_name}</TableCell>
                        <TableCell>
                          <Badge className={`text-[10px] border-0 ${t.priority === "primary" ? "bg-[#194449] text-white" : "bg-[#F4F3F5] text-[#64748B]"}`}>
                            {t.priority || "—"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${t.is_active ? "bg-[#D4EC8E] text-[#07641D]" : "bg-red-50 text-red-600"}`}>
                            {t.is_active ? "Active" : "Inactive"}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
